import React, { useCallback, useEffect, useState } from 'react';
import api from '../../utils/api';
import { formatDateTime } from '../../utils/appDateTime';
import { downloadBlob } from '../../utils/blobDownload';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import DateInput from '../ui/date-input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Loader2, Check, X, HelpCircle, ArrowRight } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { LEAVE_STATUS_LABELS } from '../portal/attendance/attendanceStatus';
import { getSchoolFeatures } from '../../utils/schoolFeatures';
import { hasPermission } from '../../utils/navPermissions';

const STATUS_VARIANT = {
  pending: 'secondary',
  pending_class_teacher: 'secondary',
  pending_admin: 'outline',
  approved: 'default',
  rejected: 'destructive',
  more_info: 'outline',
};

function statusBadgeLabel(item) {
  return item.status_label || LEAVE_STATUS_LABELS[item.status] || item.status || '—';
}

export default function LeaveManagementPage() {
  const { toast } = useToast();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [leaveWorkflow, setLeaveWorkflow] = useState({
    require_class_teacher: true,
    require_admin: true,
  });
  const [filters, setFilters] = useState({ status: '', from: '', to: '', q: '' });
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [acting, setActing] = useState(false);

  const schoolFeatures = getSchoolFeatures(user);
  const userId = parseInt(user?.id, 10);
  const isExtraLeaveApprover = userId > 0
    && (schoolFeatures.leave_extra_approver_user_ids || []).includes(userId);
  const isFinalApprover = (hasPermission(user, 'manage_leave') || isExtraLeaveApprover)
    && leaveWorkflow.require_admin;
  const canApproveLeave = hasPermission(user, 'approve_leave') || (hasPermission(user, 'manage_leave') && leaveWorkflow.require_admin);

  useEffect(() => {
    const features = getSchoolFeatures(user);
    setLeaveWorkflow({
      require_class_teacher: features.leave_require_class_teacher_approval,
      require_admin: features.leave_require_admin_approval,
    });
    setFilters((f) => {
      if (f.status) return f;
      return {
        ...f,
        status: isFinalApprover ? 'actionable' : 'pending_class_teacher',
      };
    });
  }, [user, isFinalApprover]);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: 1, per_page: 50 };
      if (filters.status && filters.status !== 'all') {
        params.status = filters.status;
        if (filters.status === 'actionable') {
          params.queue = isFinalApprover ? 'admin' : 'class_teacher';
        }
      }
      if (filters.from) params.from = filters.from;
      if (filters.to) params.to = filters.to;
      if (filters.q) params.q = filters.q;
      const res = await api.get('/leave-applications', { params });
      const data = res.data?.data ?? {};
      setItems(data.items ?? []);
      setTotal(data.total ?? 0);
      if (data.leave_workflow) {
        setLeaveWorkflow(data.leave_workflow);
      }
    } catch (e) {
      setItems([]);
      setTotal(0);
      toast({
        title: 'Could not load leave applications',
        description: e?.response?.data?.message || e?.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [filters, toast, isFinalApprover]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const openDetail = async (id) => {
    setDetailLoading(true);
    setDetail({ id });
    try {
      const res = await api.get(`/leave-applications/${id}`);
      setDetail(res.data?.data ?? null);
    } catch (e) {
      setDetail(null);
      toast({
        title: 'Could not load detail',
        description: e?.response?.data?.message || e?.message,
        variant: 'destructive',
      });
    } finally {
      setDetailLoading(false);
    }
  };

  const runAction = async () => {
    if (!detail?.id || !actionType) return;
    const needsRemarks = actionType === 'reject' || actionType === 'request-info';
    if (needsRemarks && !remarks.trim()) {
      toast({
        title: 'Remarks required',
        description: 'Please enter a message before continuing.',
        variant: 'destructive',
      });
      return;
    }
    setActing(true);
    try {
      const path = actionType === 'request-info'
        ? 'request-info'
        : actionType;
      const url = `/leave-applications/${detail.id}/${path}`;
      await api.post(url, { remarks, message: remarks });
      toast({ title: 'Updated successfully' });
      setActionType(null);
      setRemarks('');
      setDetail(null);
      loadList();
    } catch (e) {
      toast({
        title: 'Action failed',
        description: e?.response?.data?.message || e?.message,
        variant: 'destructive',
      });
    } finally {
      setActing(false);
    }
  };

  const openAttachment = async () => {
    if (!detail?.id || !detail.has_attachment) return;
    try {
      const res = await api.get(`/leave-applications/${detail.id}/attachment`, {
        responseType: 'blob',
      });
      await downloadBlob(res.data, detail.attachment_name || 'leave-attachment');
    } catch {
      toast({ title: 'Could not open attachment', variant: 'destructive' });
    }
  };

  const canRecommend = detail?.can_recommend ?? false;
  const canClassTeacherFinalize = detail?.can_class_teacher_finalize ?? false;
  const canApprove = detail?.can_final_approve ?? false;
  const canRejectOrInfo = canRecommend || canClassTeacherFinalize || canApprove;
  const actionHint = detail?.action_hint ?? null;

  const pageSubtitle = (() => {
    if (hasPermission(user, 'manage_leave') && leaveWorkflow.require_admin) {
      return 'Final approval after class teacher recommendation.';
    }
    if (hasPermission(user, 'manage_leave') && !leaveWorkflow.require_admin) {
      return 'Manage leave applications — class teachers approve in your school (admin step off).';
    }
    if (hasPermission(user, 'approve_leave') && !leaveWorkflow.require_admin) {
      return 'Approve leave for students in your class — no admin step required.';
    }
    if (hasPermission(user, 'approve_leave') && leaveWorkflow.require_admin) {
      return 'Review and recommend leave to principal or admin.';
    }
    return 'Leave applications for your school.';
  })();

  return (
    <div className="space-y-6 p-4 md:p-6 max-w-6xl mx-auto">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Leave applications</h1>
        <p className="text-muted-foreground text-sm mt-1">
          {pageSubtitle}
          {total > 0 ? ` (${total} total)` : ''}
        </p>
      </div>

      <Card>
        <CardContent className="pt-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-1">
              <Label className="text-xs">Status</Label>
              <Select
                value={filters.status || 'all'}
                onValueChange={(v) => setFilters((f) => ({ ...f, status: v === 'all' ? '' : v }))}
              >
                <SelectTrigger><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="actionable">Action needed</SelectItem>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="pending_class_teacher">Awaiting class teacher</SelectItem>
                  <SelectItem value="pending_admin">Awaiting admin</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="more_info">More info</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">From</Label>
              <DateInput
                value={filters.from}
                onChange={(v) => setFilters((f) => ({ ...f, from: v }))}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">To</Label>
              <DateInput
                value={filters.to}
                onChange={(v) => setFilters((f) => ({ ...f, to: v }))}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Search</Label>
              <Input
                placeholder="Name or admission no."
                value={filters.q}
                onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <p className="text-sm text-muted-foreground flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading…
        </p>
      ) : items.length === 0 ? (
        <div className="text-sm text-muted-foreground space-y-2">
          <p>No leave applications match your filters.</p>
          {canApproveLeave && !isFinalApprover ? (
            <p className="text-xs">
              If you expect applications here, confirm you are assigned as class teacher on the class or
              section in Class settings, then ask the student to re-login after permissions update.
            </p>
          ) : null}
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {items.map((item) => (
            <Card
              key={item.id}
              className="cursor-pointer hover:border-primary/50 transition-colors"
              onClick={() => openDetail(item.id)}
            >
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start gap-2">
                  <CardTitle className="text-base">{item.student_name || 'Student'}</CardTitle>
                  <div className="flex flex-col items-end gap-1">
                    {item.needs_my_action ? (
                      <Badge variant="default" className="text-[10px]">Action needed</Badge>
                    ) : null}
                    <Badge variant={STATUS_VARIANT[item.status] || 'secondary'}>
                      {statusBadgeLabel(item)}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="text-sm space-y-1">
                <p>{item.class_name}{item.section_name ? ` — ${item.section_name}` : ''}</p>
                <p className="text-muted-foreground">
                  {item.leave_from} → {item.leave_to} · {item.leave_type}
                </p>
                <p className="text-xs text-muted-foreground">
                  Submitted {item.created_at ? formatDateTime(item.created_at) : '—'}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) { setDetail(null); setActionType(null); } }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Leave application</DialogTitle>
          </DialogHeader>
          {detailLoading ? (
            <Loader2 className="h-6 w-6 animate-spin mx-auto" />
          ) : detail ? (
            <div className="space-y-3 text-sm">
              <p><strong>Student:</strong> {detail.student_name} ({detail.admission_no})</p>
              <p><strong>Class:</strong> {detail.class_name} {detail.section_name}</p>
              <p><strong>Dates:</strong> {detail.leave_from} — {detail.leave_to} ({detail.days_count} days)</p>
              <p><strong>Type:</strong> {detail.leave_type}</p>
              <p><strong>Reason:</strong> {detail.reason}</p>
              <p><strong>Status:</strong> {statusBadgeLabel(detail)}</p>
              {detail.class_teacher_remarks ? (
                <p><strong>Class teacher:</strong> {detail.class_teacher_remarks}</p>
              ) : null}
              {detail.reviewer_remarks ? (
                <p><strong>Remarks:</strong> {detail.reviewer_remarks}</p>
              ) : null}
              {detail.has_attachment ? (
                <p>
                  <button
                    type="button"
                    className="text-primary underline"
                    onClick={(e) => { e.stopPropagation(); openAttachment(); }}
                  >
                    View attachment
                  </button>
                </p>
              ) : null}
              {detail.audit_log?.length ? (
                <div>
                  <strong>Audit</strong>
                  <ul className="mt-1 space-y-1 text-xs text-muted-foreground">
                    {detail.audit_log.map((a) => (
                      <li key={a.id}>
                        {a.action} — {a.created_at}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {!canRejectOrInfo && actionHint ? (
                <p className="text-xs text-muted-foreground pt-2 border-t border-border">
                  {actionHint}
                </p>
              ) : null}
              {canRejectOrInfo ? (
                <div className="flex flex-wrap gap-2 pt-2">
                  {canClassTeacherFinalize ? (
                    <Button size="sm" onClick={() => setActionType('recommend')}>
                      <Check className="h-4 w-4 mr-1" />
                      Approve leave
                    </Button>
                  ) : null}
                  {canRecommend ? (
                    <Button size="sm" onClick={() => setActionType('recommend')}>
                      <ArrowRight className="h-4 w-4 mr-1" />
                      Recommend to admin
                    </Button>
                  ) : null}
                  {canApprove ? (
                    <Button size="sm" onClick={() => setActionType('approve')}>
                      <Check className="h-4 w-4 mr-1" />
                      Approve
                    </Button>
                  ) : null}
                  <Button size="sm" variant="destructive" onClick={() => setActionType('reject')}>
                    <X className="h-4 w-4 mr-1" />
                    Reject
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setActionType('request-info')}>
                    <HelpCircle className="h-4 w-4 mr-1" />
                    Request info
                  </Button>
                </div>
              ) : null}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={!!actionType} onOpenChange={(o) => { if (!o) setActionType(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionType === 'approve'
                ? 'Approve leave'
                : actionType === 'recommend' && canClassTeacherFinalize
                  ? 'Approve leave'
                  : actionType === 'recommend'
                    ? 'Recommend to admin'
                    : actionType === 'reject'
                    ? 'Reject leave'
                    : 'Request more information'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label>
              {actionType === 'approve' || actionType === 'recommend'
                ? 'Remarks (optional)'
                : 'Message / remarks'}
            </Label>
            <Textarea
              rows={3}
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              required={actionType !== 'approve' && actionType !== 'recommend'}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionType(null)}>Cancel</Button>
            <Button onClick={runAction} disabled={acting}>
              {acting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
